import React, {
  type FocusEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Select, {
  ActionMeta,
  components,
  GroupBase,
  Theme,
  OnChangeValue,
  SelectInstance,
  type InputActionMeta,
  type InputProps,
} from 'react-select';
import variables from 'scss/1-settings/colors.module.scss';
import cx from 'classnames';
import { mergeRefs } from 'shared/helpers/mergeRefs';
import InputFieldErrorMessage from 'components/InputFieldErrorMessage';
import { usePasswordManagerInputProps } from 'infrastructure/form/PasswordManagerAutofillContext';
import customStyles from './SelectFieldCustomStyles';
import useCustomSelectFieldValue from './useCustomSelectFieldValue';
import SelectFieldCustomClearIndicator from '../SelectFieldCustomClearIndicator/SelectFieldCustomClearIndicator';
import type {
  SelectFieldProps,
  SelectFieldOptionObject,
  SelectFieldOptionsArray,
} from './types';

import './SelectField.scss';

const reactSelectTheme = (theme: Theme) => ({
  ...theme,
  borderRadius: 0.2,
  borderColor: 'border-color',
  colors: {
    ...theme.colors,
    primary: variables.success,
    primary75: variables.success75,
    primary50: variables.success50,
    primary25: variables.success25,
    danger: variables.danger,
    dangerLight: variables.dangerLighter,
  },
});

const Input = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>
>(
  props: InputProps<Option, IsMulti, Group>
) => {
  const passwordManagerInputProps = usePasswordManagerInputProps();

  return (
    <components.Input
      {...props}
      autoComplete={undefined}
      {...passwordManagerInputProps}
    />
  );
};

const SelectField = <
  Option extends SelectFieldOptionObject,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  id,
  onChange,
  options,
  placeholder,
  isMulti,
  onBlur,
  isTouched = false,
  errors,
  selectFieldId,
  isClearable = true,
  isSearchable = true,
  menuPosition,
  defaultValue,
  displayValue,
  isDisabled = false,
  customRef,
  menuPlacement = 'auto',
  controlledValue,
  hideSelectedOptions,
  className,
  dataTestId = '',
  variant = 'field',
  isOptionDisabled,
  showDropdownIndicator,
  dropdownIndicatorWidthPx = 15,
  customDropdownIndicatorComponent,
  formatOptionLabel,
  customOptionComponent,
  customMultiValueComponent,
  readOnly,
  focusedPlaceholder,
  hideSelectedValueWhenMenuOpen = false,
}: SelectFieldProps<Option, IsMulti, Group>) => {
  const [selectedValue, setSelectedValue] =
    useState<OnChangeValue<Option, IsMulti>>();
  const [isFocused, setIsFocused] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const selectRef = useRef<SelectInstance<Option, IsMulti, Group>>(null);

  const customSelectFieldProps = useCustomSelectFieldValue<
    Option,
    IsMulti,
    Group
  >({
    displayValue,
    selectRef,
  });

  const onFieldChange = (
    newValue: OnChangeValue<Option, IsMulti>,
    actionMeta: ActionMeta<Option>
  ) => {
    onChange?.(newValue, actionMeta);
    setSelectedValue(newValue);
  };
  useEffect(() => {
    function isOption(value: Option | Group): value is Option {
      return 'value' in value;
    }
    if ((!defaultValue && defaultValue !== '') || !options) return;
    const defaultOption = options
      .filter(isOption)
      .find(option => option.value === defaultValue);

    setSelectedValue((isMulti ? [defaultOption] : defaultOption) as any);
  }, [defaultValue, options, isMulti]);

  const isInline = variant.startsWith('inline');

  const withCheckboxes = isMulti && !hideSelectedOptions;

  const isClearableComponentVisible =
    (!!controlledValue || !!selectedValue) && isClearable && !isDisabled;

  const onlyOptionIsSelected =
    options?.length === 1 && selectedValue === options[0];
  const shouldHideSelectedValue =
    hideSelectedValueWhenMenuOpen && isFocused && isMenuOpen;
  const currentPlaceholder =
    shouldHideSelectedValue && focusedPlaceholder
      ? focusedPlaceholder
      : placeholder;

  const handleFocus = useCallback<FocusEventHandler<HTMLInputElement>>(
    event => {
      setIsFocused(true);
      event.target.select();
    },
    []
  );

  const handleBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
    event => {
      setIsFocused(false);
      setIsMenuOpen(false);
      onBlur?.(event);
    },
    [onBlur]
  );

  const handleMenuOpen = useCallback(() => {
    setIsMenuOpen(true);
    customSelectFieldProps.onMenuOpen?.();
  }, [customSelectFieldProps]);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
    customSelectFieldProps.onMenuClose?.();
  }, [customSelectFieldProps]);

  const handleInputChange = useCallback(
    (inputValue: string, actionMeta: InputActionMeta) => {
      if (actionMeta.action === 'menu-close') {
        setIsMenuOpen(false);
      }
      return inputValue;
    },
    []
  );

  return (
    <div
      className={cx('SelectField', {
        inline: isInline,
      })}
      data-testid={dataTestId || selectFieldId}
    >
      <Select<Option, IsMulti, Group>
        {...customSelectFieldProps}
        classNamePrefix={isInline ? 'inlineRs' : 'rs'}
        className={cx(className, {
          alternative: isInline,
          withCustomValue: !!displayValue,
          withCheckboxes: isMulti && !hideSelectedOptions,
          isInvalid: !!errors && isTouched,
          menuPlacementTop: menuPlacement === 'top',
          readOnly,
        })}
        closeMenuOnSelect={!withCheckboxes}
        inputId={id}
        isClearable={isClearable}
        isSearchable={isSearchable}
        options={options}
        onChange={onFieldChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        theme={reactSelectTheme}
        placeholder={currentPlaceholder}
        isMulti={isMulti}
        menuPosition={menuPosition}
        value={controlledValue !== undefined ? controlledValue : selectedValue}
        isDisabled={
          readOnly || isDisabled || (onlyOptionIsSelected && !isClearable)
        }
        styles={
          isInline
            ? customStyles<Option, IsMulti, Group>(
                variant === 'inlineLink',
                isMulti,
                isClearableComponentVisible,
                showDropdownIndicator,
                dropdownIndicatorWidthPx
              )
            : undefined
        }
        ref={mergeRefs([selectRef, customRef]) as any}
        menuPlacement={menuPlacement}
        hideSelectedOptions={hideSelectedOptions}
        formatOptionLabel={formatOptionLabel}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        onInputChange={handleInputChange}
        controlShouldRenderValue={!shouldHideSelectedValue}
        components={{
          ...customSelectFieldProps.components,
          Input: Input as React.ComponentType<
            InputProps<Option, IsMulti, Group>
          >,
          ClearIndicator: SelectFieldCustomClearIndicator,
          ...(customDropdownIndicatorComponent
            ? {
                DropdownIndicator: customDropdownIndicatorComponent,
              }
            : {}),
          ...(customOptionComponent
            ? {
                Option: customOptionComponent,
              }
            : {}),
          ...(customMultiValueComponent
            ? {
                MultiValue: customMultiValueComponent,
              }
            : {}),
        }}
        isOptionDisabled={isOptionDisabled}
      />
      {isTouched && errors ? (
        <InputFieldErrorMessage
          dataTestId={`${dataTestId || selectFieldId}__selectInputFormFeedback`}
        >
          {errors}
        </InputFieldErrorMessage>
      ) : null}
    </div>
  );
};

export default SelectField;

export { SelectFieldProps, SelectFieldOptionObject, SelectFieldOptionsArray };
